import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import stocksService from "../../api/services/stocksService";
import store from "../store";
import {setTemporaryNotifications} from "../notifications/actions";
import {defaultNotificationCreator} from "../../templates/Notifications";
import {IColumnData, IRowData, tableShipmentsLimit} from "../../constants/Table/interfaces";

interface IStocksReducer {
    rowData: IRowData[];
    columnData: IColumnData[];
    currentPage: number;
    currentLimit: tableShipmentsLimit;
    isNeedUpdateData: boolean;
    idFilter: number|null;
    skuFilter: string|null;
    barcodeFilter: string|null;
    sizeFilter: string|null;
    nameFilter: string|null;
    brandFilter: string|null;
    notesFilter: string|null;
    greaterThanZeroFilter: string|null;
}

const initialState: IStocksReducer = {
    rowData: [],
    columnData: [],
    currentPage: 1,
    currentLimit: 10,
    isNeedUpdateData: false,
    idFilter: null,
    skuFilter: null,
    barcodeFilter: null,
    sizeFilter: null,
    nameFilter: null,
    brandFilter: null,
    notesFilter: null,
    greaterThanZeroFilter: null,
}

export const setStockToProductAtWarehouse = createAsyncThunk(
    'stocks/setStockToProductAtWarehouse',
    async (payload:any) => {
        try {
            const res = await stocksService.setStockToProductAtWarehouse(payload.sellerID, payload.productID, payload.warehouseID, payload.quantity);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }
            // local state already updated via editRowDataElement — no full reload needed
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось установить остаток",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const unsetStockOfProductAtWarehouse = createAsyncThunk(
    'stocks/unsetStockOfProductAtWarehouse',
    async (payload:any) => {
        try {
            const res = await stocksService.unsetStockOfProductAtWarehouse(payload.sellerID, payload.productID, payload.warehouseID);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }
            // local state already updated via editRowDataElement — no full reload needed
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось удалить остаток",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

const stocksStateSlice = createSlice({
    name: 'stocks',
    initialState,
    reducers: {
        /**
         * @description Removing one element from rowData
         * @param state
         * @param action
         */
        removeRowDataElement(state, action: PayloadAction<{ id: string }>) {
            state.rowData = state.rowData.filter(
                (element) => element.id !== action.payload.id
            );
        },
        /**
         * @description Editing one element in rowData
         * @param state
         * @param action
         */
        editRowDataElement(state, action: PayloadAction<IRowData>) {
            state.rowData = state.rowData.map((element) =>
                element.id !== action.payload.id ? element : action.payload
            );
        },
        /**
         * @description Setting rows
         * @param state
         * @param action
         */
        setRowData(state, action: PayloadAction<IRowData[]>) {
            state.rowData = action.payload;
        },
        /**
         * @description Set current pagination page
         * @param state
         * @param action
         */
        setCurrentPage(state, action: PayloadAction<number>) {
            state.currentPage = action.payload;
        },
        /**
         * @description Set current pagination limit of elements at page
         * @param state
         * @param action
         */
        setCurrentLimit(state, action: PayloadAction<tableShipmentsLimit>) {
            state.currentLimit = action.payload;
        },
        /**
         * @description Set flag that means is data must be updated
         * @param state
         * @param action
         */
        setIsNeedUpdateData(state, action: PayloadAction<boolean>) {
            state.isNeedUpdateData = action.payload;
        },
        setIdFilter(state, action: PayloadAction<number>) {
            state.idFilter = action.payload;
        },
        setSkuFilter(state, action: PayloadAction<string>) {
            state.skuFilter = action.payload;
        },
        setBarcodeFilter(state, action: PayloadAction<string>) {
            state.barcodeFilter = action.payload;
        },
        setSizeFilter(state, action: PayloadAction<string>) {
            state.sizeFilter = action.payload;
        },
        setNameFilter(state, action: PayloadAction<string>) {
            state.nameFilter = action.payload;
        },
        setBrandFilter(state, action: PayloadAction<string>) {
            state.brandFilter = action.payload;
        },
        setNotesFilter(state, action: PayloadAction<string>) {
            state.notesFilter = action.payload;
        },
        setGreaterThanZeroFilter(state, action: PayloadAction<string>) {
            state.greaterThanZeroFilter = action.payload;
        },
    },
});

const stocks = stocksStateSlice.reducer;

export const {
    removeRowDataElement,
    editRowDataElement,
    setIsNeedUpdateData,
    setRowData,
    setIdFilter,
    setSkuFilter,
    setBarcodeFilter,
    setSizeFilter,
    setNameFilter,
    setBrandFilter,
    setNotesFilter,
    setGreaterThanZeroFilter,
} = stocksStateSlice.actions;

export default stocks